/**
 * Ovality Check — best-fit circle on a cross-section.
 *
 * Workflow:
 *   1. Create a session (feature name + tolerance mm + optional nominal
 *      diameter for a spec-vs-actual comparison).
 *   2. Take ≥3 shots (6-12 is normal) around the cross-section. As soon
 *      as the 3rd shot lands the backend fits a Kasa 2D circle and
 *      reports centre, radius, ovality, per-point deviations.
 *
 * Live visualisations:
 *   • Section view — top-down. Fitted circle drawn in teal, nominal
 *     diameter (if supplied) drawn as a dashed grey reference, each shot
 *     marker coloured PASS/FAIL, deviation stems from the fitted circle.
 *   • Deviation strip — angle vs radial deviation, showing which sector
 *     of the section is out of round.
 */
import React, { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import {
  Plus, Crosshair, Download, X, Camera,
} from "@phosphor-icons/react";
import { station, ovality as ovalApi, target as targetApi, fmt } from "../lib/api";
import { ToolSplitLayout } from "../components/ToolSplitLayout";
import { AimPad } from "../components/AimPad";
import { ShootPanel } from "../components/ShootPanel";
import { useAuth } from "../lib/auth";
import { useLivePresence } from "../hooks/useLivePresence";


// ---------- Session list card -----------------------------------------

const SessionCard = ({ session, onOpen, onDelete }) => {
  const worstMm = Math.abs(session.max_radial_dev_mm || 0);
  const tol = session.tolerance_mm || 5;
  const state = !session.fit_ready
    ? "pending"
    : session.within_tol ? "ok" : worstMm <= tol * 2 ? "warn" : "fail";
  const color = {
    ok: "#8DC63F", warn: "#FF9F0A", fail: "#FF3B30", pending: "#009E8E",
  }[state];
  const line = session.fit_ready
    ? `Ø ${(session.radius_mm * 2).toFixed(1)} mm · ovality ${session.ovality_mm.toFixed(1)} mm · tol ±${tol.toFixed(1)}`
    : `${session.n_shots} shot${session.n_shots === 1 ? "" : "s"} · fit not ready`;
  return (
    <div
      data-testid={`oval-session-${session.id}`}
      className="group flex items-center justify-between rounded-md border border-[#2A2E39] bg-[#14161A] p-3 hover:border-[#009E8E]/50"
    >
      <button onClick={() => onOpen(session.id)} className="flex flex-1 items-start gap-3 text-left">
        <div className="h-10 w-1 rounded-full" style={{ background: color, boxShadow: `0 0 8px ${color}` }} />
        <div className="flex-1">
          <div className="text-sm font-bold uppercase text-white" style={{ fontFamily: "Chakra Petch, sans-serif" }}>
            {session.name}
          </div>
          <div className="mt-0.5 text-[10px] uppercase tracking-widest text-[#8B939A]" style={{ fontFamily: "JetBrains Mono, monospace" }}>
            {line}
          </div>
        </div>
      </button>
      <button
        data-testid={`oval-delete-${session.id}`}
        onClick={() => onDelete(session.id)}
        className="rounded-sm border border-transparent p-2 text-[#5A6065] opacity-0 transition-all group-hover:opacity-100 hover:border-[#FF3B30] hover:text-[#FF3B30]"
      >
        <X size={14} weight="bold" />
      </button>
    </div>
  );
};


// ---------- Section view — top-down XY plot with fitted circle -------

export const SectionView = ({ session }) => {
  const w = 340;
  const h = 340;
  const pad = 30;
  const plotSize = Math.min(w - 2 * pad, h - 2 * pad);
  const cx0 = w / 2;
  const cy0 = h / 2;

  const shots = session.shots || [];
  const fit = session.fit_ready;
  const orientation = session.plane_orientation;

  const radiusMm = session.radius_mm || 100;
  const worstMm = session.max_radial_dev_mm || 0;
  const viewR = radiusMm + Math.max(worstMm * 3, session.tolerance_mm * 4, 20);
  const scale = (plotSize / 2) / viewR;   // px per mm

  // Circle centre in plane (u, v) coordinates (metres). Everything on the
  // plane is drawn relative to this so the fitted circle always renders
  // dead-centre in the panel regardless of how tilted the section is.
  const cU = session.plane_center_u_m || 0;
  const cV = session.plane_center_v_m || 0;

  // Project a shot's in-plane (u, v) coords (metres) to screen pixels.
  // Convention (matching the Spool tool's Flange2D preview): +u is
  // "right as viewed from the TOTAL STATION", so we FLIP u on the
  // screen so the drawing matches what the OPERATOR sees while holding
  // the prism (mirror of the station's view). +v is "up in the plane"
  // so screen-y is negated to draw top→up.
  const projUV = (u_m, v_m) => {
    const du_mm = (u_m - cU) * 1000;
    const dv_mm = (v_m - cV) * 1000;
    return [cx0 - du_mm * scale, cy0 - dv_mm * scale];
  };

  // Compass / plane-face labels — the (u, v) frame ≈ (world east, world
  // north) only when the section is horizontal. For a tilted or vertical
  // section we swap to a "plane-face" view (Right/Left/Up/Down).
  const useCompass = !fit || orientation === "horizontal";
  const labels = useCompass
    ? { top: "N", right: "E", bottom: "S", left: "W" }
    : { top: "↑", right: "→", bottom: "↓", left: "←" };
  const panelTitle = useCompass ? "Section view (top-down)" : "Section view (looking along plane normal)";

  return (
    <div className="rounded-md border border-[#2A2E39] bg-[#0A0B0E] p-3">
      <div className="mb-2 flex items-center justify-between text-[10px] uppercase tracking-widest">
        <span className="text-[#8B939A]" style={{ fontFamily: "Chakra Petch, sans-serif" }}>
          {panelTitle}
        </span>
        <span className="text-[#8B939A]" style={{ fontFamily: "JetBrains Mono, monospace" }}>
          view ± {viewR.toFixed(0)} mm
        </span>
      </div>
      <svg
        data-testid="oval-section-view"
        viewBox={`0 0 ${w} ${h}`}
        className="w-full"
        style={{ maxHeight: 380 }}
      >
        {/* Arrowhead markers — one per pass/fail colour. */}
        <defs>
          <marker id="ov-arrow-pass" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="6" markerHeight="6" orient="auto-start-reverse">
            <path d="M0,0 L10,5 L0,10 Z" fill="#8DC63F" />
          </marker>
          <marker id="ov-arrow-fail" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="6" markerHeight="6" orient="auto-start-reverse">
            <path d="M0,0 L10,5 L0,10 Z" fill="#FF3B30" />
          </marker>
        </defs>
        {/* Frame */}
        <rect x={pad} y={pad} width={w - 2 * pad} height={h - 2 * pad} fill="none" stroke="#2A2E39" strokeWidth="0.5" />
        {/* Crosshair at fitted center */}
        <line x1={pad} y1={cy0} x2={w - pad} y2={cy0} stroke="#2A2E39" strokeWidth="0.5" />
        <line x1={cx0} y1={pad} x2={cx0} y2={h - pad} stroke="#2A2E39" strokeWidth="0.5" />
        <text x={cx0} y={pad + 10} textAnchor="middle" fill="#4A5054" fontSize="8" fontFamily="JetBrains Mono, monospace">{labels.top}</text>
        <text x={w - pad - 4} y={cy0 - 3} textAnchor="end" fill="#4A5054" fontSize="8" fontFamily="JetBrains Mono, monospace">{labels.right}</text>
        <text x={cx0} y={h - pad - 4} textAnchor="middle" fill="#4A5054" fontSize="8" fontFamily="JetBrains Mono, monospace">{labels.bottom}</text>
        <text x={pad + 4} y={cy0 - 3} fill="#4A5054" fontSize="8" fontFamily="JetBrains Mono, monospace">{labels.left}</text>

        {fit ? (
          <>
            {/* Tolerance envelope — inner & outer bands around fitted R */}
            <circle cx={cx0} cy={cy0} r={(radiusMm + session.tolerance_mm) * scale} fill="none" stroke="#8DC63F" strokeWidth="0.6" strokeDasharray="3 3" opacity="0.5" />
            <circle cx={cx0} cy={cy0} r={Math.max(1, (radiusMm - session.tolerance_mm) * scale)} fill="none" stroke="#8DC63F" strokeWidth="0.6" strokeDasharray="3 3" opacity="0.5" />

            {/* Nominal (target) diameter — if provided */}
            {session.target_diameter_mm && (
              <circle
                cx={cx0}
                cy={cy0}
                r={(session.target_diameter_mm / 2) * scale}
                fill="none"
                stroke="#8B939A"
                strokeWidth="0.7"
                strokeDasharray="4 3"
                opacity="0.7"
              />
            )}

            {/* Fitted circle */}
            <circle cx={cx0} cy={cy0} r={radiusMm * scale} fill="none" stroke="#009E8E" strokeWidth="1.5" />
            {/* Fitted centre */}
            <circle cx={cx0} cy={cy0} r="3" fill="#009E8E" />

            {/* Diameter chords — measured AS-BUILT diameter across the
                section at every 45°. Radial distances between consecutive
                shots are linearly interpolated at the target angle.
                Min diameter drawn amber, max drawn red, others in teal so
                the surveyor sees the ovality axis at a glance. */}
            {(() => {
              const sorted = shots
                .filter((s) => s.angle_deg != null && s.radial_mm != null)
                .sort((a, b) => a.angle_deg - b.angle_deg);
              if (sorted.length < 3) return null;
              const interp = (targetDeg) => {
                const norm = ((targetDeg % 360) + 360) % 360;
                for (let k = 0; k < sorted.length; k++) {
                  const a1 = sorted[k].angle_deg;
                  const a2 = sorted[(k + 1) % sorted.length].angle_deg;
                  let span = a2 - a1;
                  if (span <= 0) span += 360;
                  let offset = norm - a1;
                  if (offset < 0) offset += 360;
                  if (offset <= span + 1e-6) {
                    const t = span > 0 ? offset / span : 0;
                    const r1 = sorted[k].radial_mm;
                    const r2 = sorted[(k + 1) % sorted.length].radial_mm;
                    return r1 + t * (r2 - r1);
                  }
                }
                return sorted[0].radial_mm;
              };
              // Compute 4 unique diameter chords (0/45/90/135°) — each is
              // the pair θ↔θ+180°. Track min/max for highlight colouring.
              const diameterAngles = [0, 45, 90, 135];
              const diameters = diameterAngles.map((deg) => {
                const r1 = interp(deg);
                const r2 = interp(deg + 180);
                return { deg, r1, r2, d_mm: r1 + r2 };
              });
              const minD = Math.min(...diameters.map((d) => d.d_mm));
              const maxD = Math.max(...diameters.map((d) => d.d_mm));
              return (
                <g data-testid="oval-diameter-chords">
                  {diameters.map(({ deg, r1, r2, d_mm }) => {
                    // Sector angle uses atan2(u, v) so 0° = +v (top).
                    // On screen: u is FLIPPED, v is flipped. So the two
                    // endpoints of the chord map like the shot markers.
                    const rad = (deg * Math.PI) / 180;
                    const u1 = r1 * Math.sin(rad);
                    const v1 = r1 * Math.cos(rad);
                    const u2 = -r2 * Math.sin(rad);
                    const v2 = -r2 * Math.cos(rad);
                    const x1 = cx0 - u1 * scale;
                    const y1 = cy0 - v1 * scale;
                    const x2 = cx0 - u2 * scale;
                    const y2 = cy0 - v2 * scale;
                    const isMin = Math.abs(d_mm - minD) < 1e-6 && maxD - minD > 0.1;
                    const isMax = Math.abs(d_mm - maxD) < 1e-6 && maxD - minD > 0.1;
                    const color = isMax ? "#FF3B30" : isMin ? "#FF9F0A" : "#009E8E";
                    const weight = (isMin || isMax) ? 1.2 : 0.6;
                    // Place the label just outside the "top-half" end.
                    const midX = (x1 + x2) / 2;
                    const midY = (y1 + y2) / 2;
                    // Offset label perpendicular to chord + toward top.
                    const dx = x2 - x1;
                    const dy = y2 - y1;
                    const len = Math.hypot(dx, dy) || 1;
                    // Choose the end that lies higher on screen (smaller y) —
                    // gives less-crowded labels since text sits above the circle.
                    const topX = y1 < y2 ? x1 : x2;
                    const topY = y1 < y2 ? y1 : y2;
                    // Push label outward along the chord direction.
                    const outDx = (topX - midX) / (len / 2);
                    const outDy = (topY - midY) / (len / 2);
                    const labelX = topX + outDx * 10;
                    const labelY = topY + outDy * 10;
                    return (
                      <g key={deg}>
                        <line
                          x1={x1} y1={y1} x2={x2} y2={y2}
                          stroke={color}
                          strokeWidth={weight}
                          strokeDasharray={(isMin || isMax) ? "0" : "3 3"}
                          opacity={isMin || isMax ? 0.9 : 0.4}
                        />
                        <text
                          x={labelX}
                          y={labelY}
                          textAnchor="middle"
                          fill={color}
                          fontSize={isMin || isMax ? 10 : 8}
                          fontFamily="JetBrains Mono, monospace"
                          fontWeight={isMin || isMax ? 700 : 400}
                        >
                          {d_mm.toFixed(1)}
                        </text>
                      </g>
                    );
                  })}
                </g>
              );
            })()}

            {/* Shot markers + deviation arrows FROM design TO actual */}
            {shots.map((s, i) => {
              if (s.u_m == null || s.v_m == null) return null;
              const [px, py] = projUV(s.u_m, s.v_m);
              // Direction from fitted centre to actual shot in plane coords.
              const du = s.u_m - cU;
              const dv = s.v_m - cV;
              const rlen = Math.hypot(du, dv);
              const nu = rlen > 0 ? du / rlen : 0;
              const nv = rlen > 0 ? dv / rlen : 0;
              // "Design" radius: nominal Ø if provided, else fitted radius.
              const designR_m = session.target_diameter_mm
                ? (session.target_diameter_mm / 2) / 1000
                : (session.radius_mm / 1000);
              const designU = cU + nu * designR_m;
              const designV = cV + nv * designR_m;
              const [dxs, dys] = projUV(designU, designV);
              const devFromDesign_mm = (rlen - designR_m) * 1000;
              const ok = Math.abs(devFromDesign_mm) <= session.tolerance_mm;
              const stroke = ok ? "#8DC63F" : "#FF3B30";
              const marker = ok ? "url(#ov-arrow-pass)" : "url(#ov-arrow-fail)";
              const stemLen = Math.hypot(px - dxs, py - dys);
              return (
                <g key={s.id}>
                  {stemLen >= 6 && (
                    <line
                      x1={dxs}
                      y1={dys}
                      x2={px}
                      y2={py}
                      stroke={stroke}
                      strokeWidth="1.8"
                      opacity="0.9"
                      markerEnd={marker}
                    />
                  )}
                  <circle cx={dxs} cy={dys} r="1.5" fill={stroke} opacity="0.6" />
                  <circle cx={px} cy={py} r="4.5" fill={stroke} stroke="#0A0B0E" strokeWidth="1.5" />
                  <text
                    x={px + 7}
                    y={py + 3}
                    fill={stroke}
                    fontSize="9"
                    fontFamily="JetBrains Mono, monospace"
                  >
                    {s.label || `#${i + 1}`}
                  </text>
                </g>
              );
            })}
          </>
        ) : (
          <>
            <text
              x={cx0}
              y={cy0 - 8}
              textAnchor="middle"
              fill="#5A6065"
              fontSize="11"
              fontFamily="Chakra Petch, sans-serif"
            >
              {shots.length < 3
                ? `Take ${3 - shots.length} more shot${3 - shots.length === 1 ? "" : "s"} to fit a circle`
                : "Fit failed — spread shots wider around the section"}
            </text>
            {/* Show raw shots even before fit — plot around a rough centroid */}
            {shots.length > 0 && (() => {
              const cx = shots.reduce((s, p) => s + p.x, 0) / shots.length;
              const cy = shots.reduce((s, p) => s + p.y, 0) / shots.length;
              const rMax = Math.max(...shots.map((p) => Math.hypot(p.x - cx, p.y - cy))) || 0.001;
              const s0 = (plotSize / 2) / (rMax * 1000 * 1.3);
              return shots.map((s, i) => {
                const px = cx0 + (s.x - cx) * 1000 * s0;
                const py = cy0 - (s.y - cy) * 1000 * s0;
                return (
                  <g key={s.id}>
                    <circle cx={px} cy={py} r="4" fill="#009E8E" stroke="#0A0B0E" strokeWidth="1.5" />
                    <text x={px + 7} y={py + 3} fill="#8B939A" fontSize="9" fontFamily="JetBrains Mono, monospace">
                      {s.label || `#${i + 1}`}
                    </text>
                  </g>
                );
              });
            })()}
          </>
        )}
      </svg>
      {fit && (
        <div className="mt-1 flex flex-wrap items-center justify-center gap-3 text-[9px] uppercase tracking-widest">
          <span className="flex items-center gap-1 text-[#009E8E]">
            <span className="h-2 w-4 rounded-sm border border-[#009E8E]" /> Fitted circle
          </span>
          <span className="flex items-center gap-1 text-[#8DC63F]">
            <span className="h-2 w-4 rounded-sm border border-[#8DC63F] border-dashed" /> ±{session.tolerance_mm.toFixed(1)} mm tol
          </span>
          {session.target_diameter_mm && (
            <span className="flex items-center gap-1 text-[#8B939A]">
              <span className="h-2 w-4 rounded-sm border border-[#8B939A] border-dashed" /> Nominal Ø{session.target_diameter_mm.toFixed(0)}
            </span>
          )}
        </div>
      )}
    </div>
  );
};


// ---------- Flatness view — top-down heat map + point table ---------
//
// Fits a plane through the 3D shot cloud, then shows each point in a
// top-down view of the section (looking along the plane normal). Each
// point is coloured by its signed distance from the best-fit plane:
//   • Green  — within tolerance
//   • Red    — above the plane (+ residual)
//   • Blue   — below the plane (− residual)
// A compact table on the right lists the exact mm deviation for every
// point so the surveyor can read the numbers straight into the report.
// A tilt readout above the heat map summarises how the whole section
// sits vs. a horizontal reference (or vs. horizontal for tilted /
// vertical sections, the normal-vector bearing).

const bearingLabel = (deg) => {
  const labels = ["N", "NE", "E", "SE", "S", "SW", "W", "NW"];
  return labels[Math.round(deg / 45) % 8];
};

export const PlaneView = ({ session, planeMode = "fit", onPlaneModeChange }) => {
  const shots = session.shots || [];
  const ready = session.plane_ready;
  const tol = session.tolerance_mm || 5;
  const clAvailable = !!session.pipe_axis_ready;
  // Effective mode: fall back to "fit" if CL isn't available yet.
  const mode = clAvailable && planeMode === "cl" ? "cl" : "fit";
  const worstMm = mode === "cl"
    ? (session.cl_plane_max_residual_mm || 0)
    : (session.plane_max_residual_mm || 0);
  const rmsMm = mode === "cl"
    ? (session.cl_plane_rms_mm || 0)
    : (session.plane_rms_mm || 0);

  // ---- Top-down projection of the plane (looking along its normal).
  //      Uses the same convention as SectionView so the two panels
  //      overlay conceptually: +u drawn RIGHT-flipped so the operator's
  //      view matches what they see standing at the prism.
  const cU = session.plane_center_u_m || 0;
  const cV = session.plane_center_v_m || 0;
  const radiusMm = session.radius_mm || 100;
  const viewR = radiusMm * 1.25 + Math.max(worstMm * 2, tol * 3, 20);

  const w = 320, h = 320, pad = 26;
  const plotSize = Math.min(w - 2 * pad, h - 2 * pad);
  const cx0 = w / 2, cy0 = h / 2;
  const scale = (plotSize / 2) / viewR;

  const projUV = (u_m, v_m) => {
    const du_mm = (u_m - cU) * 1000;
    const dv_mm = (v_m - cV) * 1000;
    return [cx0 - du_mm * scale, cy0 - dv_mm * scale];
  };

  // Colour ramp: green (in tol) / red (above plane) / blue (below).
  const colorFor = (mm) => {
    if (Math.abs(mm) <= tol) return "#8DC63F";
    return mm > 0 ? "#FF3B30" : "#3B9BFF";
  };

  const useCompass = !ready || session.plane_orientation === "horizontal";
  const labels = useCompass
    ? { t: "N", r: "E", b: "S", l: "W" }
    : { t: "↑", r: "→", b: "↓", l: "←" };

  const pts = shots
    .filter((s) => s.u_m != null && s.v_m != null)
    .map((s, i) => ({
      id: s.id,
      idx: i,
      label: s.label || `P${i}`,
      u_m: s.u_m,
      v_m: s.v_m,
      // The residual we render depends on the selected plane mode:
      //   "fit" — signed distance from the best-fit plane
      //   "cl"  — signed distance from the pipe-CL plane
      z_residual_mm: (mode === "cl" ? s.cl_residual_mm : s.z_residual_mm) || 0,
    }));

  return (
    <div className="rounded-md border border-[#2A2E39] bg-[#0A0B0E] p-3">
      <div className="mb-2 flex items-center justify-between gap-2 text-[10px] uppercase tracking-widest">
        <span className="text-[#8B939A]" style={{ fontFamily: "Chakra Petch, sans-serif" }}>
          Flatness · deviation from {mode === "cl" ? "pipe CL plane" : "best-fit plane"}
        </span>
        <div className="flex items-center gap-2">
          {/* Best-fit ↔ CL toggle. Only shown when a pipe axis exists. */}
          {clAvailable && (
            <div className="flex overflow-hidden rounded-sm border border-[#2A2E39] bg-[#14161A]" data-testid="oval-plane-mode-toggle">
              <button
                data-testid="oval-plane-mode-fit"
                onClick={() => onPlaneModeChange && onPlaneModeChange("fit")}
                className={`px-2 py-1 text-[9px] font-bold tracking-widest transition-colors ${
                  mode === "fit"
                    ? "bg-[#009E8E]/20 text-[#009E8E]"
                    : "text-[#8B939A] hover:text-white"
                }`}
                style={{ fontFamily: "Chakra Petch, sans-serif" }}
              >
                Best-fit
              </button>
              <button
                data-testid="oval-plane-mode-cl"
                onClick={() => onPlaneModeChange && onPlaneModeChange("cl")}
                className={`px-2 py-1 text-[9px] font-bold tracking-widest transition-colors ${
                  mode === "cl"
                    ? "bg-[#009E8E]/20 text-[#009E8E]"
                    : "text-[#8B939A] hover:text-white"
                }`}
                style={{ fontFamily: "Chakra Petch, sans-serif" }}
              >
                Pipe CL
              </button>
            </div>
          )}
          {ready && (
            <span className="text-[#8B939A]" style={{ fontFamily: "JetBrains Mono, monospace" }}>
              tol ±{tol.toFixed(1)} mm
            </span>
          )}
        </div>
      </div>
      {!ready ? (
        <div className="flex h-[300px] items-center justify-center text-[11px] text-[#5A6065]" style={{ fontFamily: "Chakra Petch, sans-serif" }}>
          Plane fit available after 3+ shots
        </div>
      ) : (
        <div className="space-y-3">
          {/* Tilt readouts — depends on plane orientation.
              • For a near-horizontal section (chimney, tank ring), show
                E/W and N/S slopes in mm/m — the surveyor's natural way
                of describing levelness.
              • For a tilted or vertical section (horizontal pipe end,
                sloping flange), those slopes blow up or vanish. Instead
                report the plane inclination + the bearing the section
                faces (i.e. the direction the fitted normal points). */}
          {session.plane_orientation === "horizontal" ? (
            <div className="mb-2 grid grid-cols-2 gap-2 rounded-sm border border-[#2A2E39] bg-[#14161A] p-2">
              <div>
                <div className="text-[9px] uppercase tracking-widest text-[#8B939A]">E/W tilt</div>
                <div className="mt-0.5 font-mono text-sm text-white" data-testid="oval-ew-tilt">
                  {(session.ew_slope_mm_per_m || 0) > 0 ? "+" : ""}
                  {(session.ew_slope_mm_per_m || 0).toFixed(2)} mm/m
                  <span className="ml-2 text-[10px] text-[#8B939A]">
                    ({(session.ew_tilt_deg || 0).toFixed(3)}°)
                  </span>
                </div>
                <div className="text-[9px] uppercase tracking-widest text-[#5A6065]">
                  {(session.ew_slope_mm_per_m || 0) > 0.001
                    ? "rises →E"
                    : (session.ew_slope_mm_per_m || 0) < -0.001
                      ? "rises →W"
                      : "level"}
                </div>
              </div>
              <div>
                <div className="text-[9px] uppercase tracking-widest text-[#8B939A]">N/S tilt</div>
                <div className="mt-0.5 font-mono text-sm text-white" data-testid="oval-ns-tilt">
                  {(session.ns_slope_mm_per_m || 0) > 0 ? "+" : ""}
                  {(session.ns_slope_mm_per_m || 0).toFixed(2)} mm/m
                  <span className="ml-2 text-[10px] text-[#8B939A]">
                    ({(session.ns_tilt_deg || 0).toFixed(3)}°)
                  </span>
                </div>
                <div className="text-[9px] uppercase tracking-widest text-[#5A6065]">
                  {(session.ns_slope_mm_per_m || 0) > 0.001
                    ? "rises →N"
                    : (session.ns_slope_mm_per_m || 0) < -0.001
                      ? "rises →S"
                      : "level"}
                </div>
              </div>
            </div>
          ) : (
            <div className="mb-2 grid grid-cols-2 gap-2 rounded-sm border border-[#2A2E39] bg-[#14161A] p-2">
              <div>
                <div className="text-[9px] uppercase tracking-widest text-[#8B939A]">Plane inclination</div>
                <div className="mt-0.5 font-mono text-sm text-white" data-testid="oval-plane-inclination">
                  {(session.plane_inclination_deg || 0).toFixed(2)}° from horizontal
                </div>
                <div className="text-[9px] uppercase tracking-widest text-[#5A6065]">
                  {session.plane_orientation === "vertical" ? "VERTICAL SECTION" : "TILTED SECTION"}
                </div>
              </div>
              <div>
                <div className="text-[9px] uppercase tracking-widest text-[#8B939A]">Section faces</div>
                <div className="mt-0.5 font-mono text-sm text-white" data-testid="oval-normal-bearing">
                  {session.plane_normal_bearing_deg != null
                    ? `${session.plane_normal_bearing_deg.toFixed(1)}° ${bearingLabel(session.plane_normal_bearing_deg)}`
                    : "—"}
                </div>
                <div className="text-[9px] uppercase tracking-widest text-[#5A6065]">
                  Normal-vector bearing
                </div>
              </div>
            </div>
          )}

          {/* Top-down heat map + adjacent per-point deviation table.
              Left panel: SVG of the section viewed along the plane
              normal. Each shot is a coloured dot at its (u, v) position
              — green means within tolerance, red = above the plane,
              blue = below. The fitted circle outline is drawn dashed
              teal underneath so the operator sees the section footprint.
              Right panel: a compact table of every point's z-residual
              in mm, so the surveyor can read exact numbers straight
              into the report. */}
          <div className="grid gap-3 sm:grid-cols-[1fr,140px]">
            <div>
              <svg
                data-testid="oval-plane-view"
                viewBox={`0 0 ${w} ${h}`}
                className="w-full"
                style={{ maxHeight: 320 }}
              >
                {/* Frame + crosshair */}
                <rect x={pad} y={pad} width={w - 2 * pad} height={h - 2 * pad} fill="none" stroke="#2A2E39" strokeWidth="0.5" />
                <line x1={pad} y1={cy0} x2={w - pad} y2={cy0} stroke="#2A2E39" strokeWidth="0.5" />
                <line x1={cx0} y1={pad} x2={cx0} y2={h - pad} stroke="#2A2E39" strokeWidth="0.5" />
                <text x={cx0} y={pad + 10} textAnchor="middle" fill="#4A5054" fontSize="8" fontFamily="JetBrains Mono, monospace">{labels.t}</text>
                <text x={w - pad - 4} y={cy0 - 3} textAnchor="end" fill="#4A5054" fontSize="8" fontFamily="JetBrains Mono, monospace">{labels.r}</text>
                <text x={cx0} y={h - pad - 4} textAnchor="middle" fill="#4A5054" fontSize="8" fontFamily="JetBrains Mono, monospace">{labels.b}</text>
                <text x={pad + 4} y={cy0 - 3} fill="#4A5054" fontSize="8" fontFamily="JetBrains Mono, monospace">{labels.l}</text>

                {/* Fitted-circle outline (dashed) for context */}
                <circle cx={cx0} cy={cy0} r={radiusMm * scale} fill="none" stroke="#009E8E" strokeWidth="0.6" strokeDasharray="3 3" opacity="0.4" />
                <circle cx={cx0} cy={cy0} r="2" fill="#009E8E" />

                {/* Points — colour encodes signed z-residual */}
                {pts.map((p) => {
                  const [px, py] = projUV(p.u_m, p.v_m);
                  const color = colorFor(p.z_residual_mm);
                  const sign = p.z_residual_mm >= 0 ? "+" : "";
                  return (
                    <g key={p.id} data-testid={`oval-plane-pt-${p.id}`}>
                      <circle cx={px} cy={py} r="7" fill={color} stroke="#0A0B0E" strokeWidth="1.5" opacity="0.95" />
                      <text x={px} y={py - 11} textAnchor="middle" fill={color} fontSize="9" fontFamily="JetBrains Mono, monospace" fontWeight="700">
                        {p.label}
                      </text>
                      <text x={px} y={py + 16} textAnchor="middle" fill={color} fontSize="8" fontFamily="JetBrains Mono, monospace">
                        {sign}{p.z_residual_mm.toFixed(1)}
                      </text>
                    </g>
                  );
                })}
              </svg>
              {/* Colour legend */}
              <div className="mt-1 flex flex-wrap items-center justify-center gap-3 text-[9px] uppercase tracking-widest" style={{ fontFamily: "Chakra Petch, sans-serif" }}>
                <span className="flex items-center gap-1 text-[#3B9BFF]">
                  <span className="inline-block h-2 w-2 rounded-full" style={{ background: "#3B9BFF" }} />
                  below
                </span>
                <span className="flex items-center gap-1 text-[#8DC63F]">
                  <span className="inline-block h-2 w-2 rounded-full" style={{ background: "#8DC63F" }} />
                  in tol
                </span>
                <span className="flex items-center gap-1 text-[#FF3B30]">
                  <span className="inline-block h-2 w-2 rounded-full" style={{ background: "#FF3B30" }} />
                  above
                </span>
              </div>
            </div>

            {/* Per-point deviation table */}
            <div className="overflow-hidden rounded-sm border border-[#2A2E39] bg-[#14161A]">
              <div className="border-b border-[#2A2E39] bg-[#0A0B0E] px-2 py-1.5 text-[9px] uppercase tracking-widest text-[#8B939A]" style={{ fontFamily: "Chakra Petch, sans-serif" }}>
                Point · Δ mm
              </div>
              <div className="max-h-[280px] overflow-y-auto">
                <table className="w-full text-[10px]" style={{ fontFamily: "JetBrains Mono, monospace" }}>
                  <tbody>
                    {pts.length === 0 ? (
                      <tr>
                        <td className="px-2 py-3 text-center text-[#5A6065]">—</td>
                      </tr>
                    ) : pts.map((p) => {
                      const color = colorFor(p.z_residual_mm);
                      const sign = p.z_residual_mm >= 0 ? "+" : "";
                      return (
                        <tr key={p.id} data-testid={`oval-plane-row-${p.id}`} className="border-b border-[#1A1D24]">
                          <td className="px-2 py-1 text-white">{p.label}</td>
                          <td className="px-2 py-1 text-right font-bold" style={{ color }}>
                            {sign}{p.z_residual_mm.toFixed(2)}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <div className="border-t border-[#2A2E39] bg-[#0A0B0E] px-2 py-1.5 text-[9px] uppercase tracking-widest text-[#5A6065]" style={{ fontFamily: "JetBrains Mono, monospace" }}>
                RMS {rmsMm.toFixed(2)}<br />Max {worstMm.toFixed(2)} mm
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};


// ---------- Shot table row -------------------------------------------

const ShotRow = ({ shot, index, tol, onDelete }) => {
  const chip = shot.within_tol == null
    ? { label: "—", cls: "text-[#8B939A] border-[#8B939A]/40" }
    : shot.within_tol
      ? { label: "PASS", cls: "text-[#8DC63F] border-[#8DC63F]/40 bg-[#8DC63F]/10" }
      : { label: "FAIL", cls: "text-[#FF3B30] border-[#FF3B30]/40 bg-[#FF3B30]/10" };
  return (
    <tr data-testid={`oval-shot-row-${shot.id}`} className="border-b border-[#1A1D24]">
      <td className="p-2 text-white">#{index + 1}</td>
      <td className="p-2 text-white">{shot.label || "—"}</td>
      <td className="p-2 text-right font-mono">{shot.angle_deg == null ? "—" : `${shot.angle_deg.toFixed(0)}°`}</td>
      <td className="p-2 text-right font-mono">{fmt(shot.radial_mm, 1)}</td>
      <td className="p-2 text-right font-mono font-bold text-white">
        {shot.deviation_mm == null
          ? "—"
          : `${shot.deviation_mm > 0 ? "+" : ""}${shot.deviation_mm.toFixed(1)}`}
      </td>
      <td className="p-2 text-right font-mono">
        {shot.z_residual_mm == null
          ? "—"
          : `${shot.z_residual_mm > 0 ? "+" : ""}${shot.z_residual_mm.toFixed(1)}`}
      </td>
      <td className="p-2 text-center">
        <span className={`inline-block rounded-sm border px-2 py-0.5 text-[9px] font-bold uppercase tracking-widest ${chip.cls}`}>
          {chip.label}
        </span>
      </td>
      <td className="p-2 text-right">
        <button
          data-testid={`oval-shot-delete-${shot.id}`}
          onClick={() => onDelete(shot.id)}
          className="rounded-sm border border-transparent p-1 text-[#5A6065] hover:border-[#FF3B30] hover:text-[#FF3B30]"
          title="Delete shot"
        >
          <X size={12} weight="bold" />
        </button>
      </td>
    </tr>
  );
};


// ---------- Print report (A4 field report) ---------------------------
//
// Rendered only when the browser is in print mode (see `.print-only`
// CSS in the parent). Uses SectionView + PlaneView from above so the
// diagrams look identical to what the surveyor saw on screen, but with
// print-safe colour rewrites applied by the print stylesheet.

const PrintReport = ({ session, user, activeTarget }) => {
  const s = session;
  const now = new Date();
  const shots = s.shots || [];
  const refs = s.reference_shots || [];
  const checks = shots.filter((sh) => sh.deviation_mm != null);
  const pass = s.within_tol === true;
  const anyDone = s.fit_ready && checks.length > 0;

  const kv = (label, value, mono = true) => (
    <div>
      <div style={{ fontSize: "7pt", letterSpacing: "0.15em", textTransform: "uppercase", color: "#666" }}>
        {label}
      </div>
      <div style={{ fontSize: "10pt", fontFamily: mono ? "JetBrains Mono, monospace" : "inherit", marginTop: "0.5mm" }}>
        {value}
      </div>
    </div>
  );

  return (
    <div>
      {/* -------- Masthead -------- */}
      <div className="fx-avoid-break">
        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "5mm" }}>
            <img src="/fabaxis-mark-light.png" alt="" style={{ height: "16mm" }} />
            <div>
              <div className="fx-h1" style={{ fontSize: "18pt", color: "#0A0B0E" }}>
                <span style={{ color: "#B0000A" }}>Fab</span>Axis
              </div>
              <div style={{ fontSize: "8pt", letterSpacing: "0.25em", textTransform: "uppercase", color: "#555" }}>
                Field Controller · Wilson Design
              </div>
            </div>
          </div>
          <div style={{ textAlign: "right" }}>
            <div style={{ fontSize: "7pt", letterSpacing: "0.25em", textTransform: "uppercase", color: "#666" }}>
              Report reference
            </div>
            <div className="fx-mono" style={{ fontSize: "9pt", marginTop: "0.5mm" }}>
              FA-OVL-{s.id.slice(0, 8).toUpperCase()}
            </div>
            <div style={{ fontSize: "8pt", marginTop: "1mm", color: "#555" }}>
              {now.toLocaleDateString(undefined, { day: "2-digit", month: "short", year: "numeric" })} ·{" "}
              {now.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" })}
            </div>
          </div>
        </div>
        <div className="fx-thick" style={{ marginTop: "3mm" }} />
        <div style={{ marginTop: "4mm", display: "flex", alignItems: "flex-end", justifyContent: "space-between" }}>
          <div>
            <div className="fx-h1" style={{ fontSize: "22pt", lineHeight: 1.1 }}>
              Ovality &amp; Flatness Inspection
            </div>
            <div style={{ fontSize: "13pt", marginTop: "1mm", color: "#333" }}>
              {s.name}
            </div>
          </div>
          {/* Executive PASS/FAIL banner */}
          {anyDone && (
            <div
              className={pass ? "fx-tint-pass" : "fx-tint-fail"}
              style={{
                padding: "3mm 6mm",
                borderRadius: "2mm",
                border: `0.5pt solid ${pass ? "#5A8A3D" : "#A33"}`,
              }}
            >
              <div style={{ fontSize: "7pt", letterSpacing: "0.25em", textTransform: "uppercase", opacity: 0.75 }}>
                Result
              </div>
              <div className="fx-h1" style={{ fontSize: "20pt", lineHeight: 1 }}>
                {pass ? "Pass" : "Fail"}
              </div>
              <div style={{ fontSize: "7.5pt", marginTop: "1mm" }}>
                Tolerance ± {s.tolerance_mm.toFixed(2)} mm
              </div>
            </div>
          )}
        </div>
      </div>

      {/* -------- Metadata strip -------- */}
      <div className="fx-avoid-break" style={{ marginTop: "5mm" }}>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "4mm" }}>
          <div className="fx-card mute">
            {kv("Surveyor", user?.username || "—", false)}
            <div style={{ marginTop: "1.5mm" }}>{kv("Instrument", "Leica TS16")}</div>
          </div>
          <div className="fx-card mute">
            {kv("Target", activeTarget?.label || "—", false)}
            <div style={{ marginTop: "1.5mm" }}>
              {kv("⊥ offset", `${(activeTarget?.perpendicular_mm ?? 0).toFixed(2)} mm`)}
            </div>
          </div>
          <div className="fx-card mute">
            {kv("Nominal Ø", s.target_diameter_mm ? `${s.target_diameter_mm.toFixed(1)} mm` : "not specified")}
            <div style={{ marginTop: "1.5mm" }}>
              {kv("Shots · Refs", `${shots.length} · ${refs.length}`)}
            </div>
          </div>
        </div>
      </div>

      {/* -------- Key metrics summary -------- */}
      <h2>Executive Summary</h2>
      <div className="fx-avoid-break">
        <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: "3mm" }}>
          <div className="fx-card">
            {kv("Fitted Ø", s.radius_mm != null ? `${(s.radius_mm * 2).toFixed(2)} mm` : "—")}
          </div>
          <div className="fx-card">
            {kv("Ovality", s.ovality_mm != null ? `${s.ovality_mm.toFixed(2)} mm` : "—")}
          </div>
          <div className="fx-card">
            {kv("Max radial dev", s.max_radial_dev_mm != null ? `${s.max_radial_dev_mm.toFixed(2)} mm` : "—")}
          </div>
          <div className="fx-card">
            {kv("RMS dev", s.rms_dev_mm != null ? `${s.rms_dev_mm.toFixed(2)} mm` : "—")}
          </div>
          <div className="fx-card">
            {kv("Plane tilt", s.tilt_total_deg != null ? `${s.tilt_total_deg.toFixed(3)}°` : "—")}
          </div>
          <div className="fx-card">
            {kv("Plane RMS", s.plane_rms_mm != null ? `${s.plane_rms_mm.toFixed(2)} mm` : "—")}
          </div>
          <div className="fx-card">
            {kv("Plane max resid", s.plane_max_residual_mm != null ? `${s.plane_max_residual_mm.toFixed(2)} mm` : "—")}
          </div>
          <div className="fx-card">
            {kv("Squareness", s.squareness_deg != null ? `${s.squareness_deg.toFixed(2)}°` : "—")}
          </div>
        </div>
      </div>

      {/* -------- Cross-section diagram -------- */}
      <h2>Cross-section (best-fit circle)</h2>
      <div className="fx-chart-frame fx-avoid-break">
        <SectionView session={s} />
      </div>

      {/* -------- Flatness plane diagram -------- */}
      <h2>Flatness plane</h2>
      <div className="fx-chart-frame fx-avoid-break">
        <PlaneView session={s} planeMode="fit" />
      </div>

      {/* -------- Shot table -------- */}
      <h2>Detailed shot measurements</h2>
      <table className="fx-shots">
        <thead>
          <tr>
            <th className="left">#</th>
            <th className="left">Label</th>
            <th>X (m)</th>
            <th>Y (m)</th>
            <th>Z (m)</th>
            <th>Angle</th>
            <th>Radial (mm)</th>
            <th>Δr (mm)</th>
            <th>Δz (mm)</th>
            <th className="left">Result</th>
          </tr>
        </thead>
        <tbody>
          {shots.map((sh, i) => (
            <tr key={sh.id} className={sh.within_tol === false ? "fail" : ""}>
              <td className="left">{i + 1}</td>
              <td className="left">{sh.label || "—"}</td>
              <td>{sh.x.toFixed(3)}</td>
              <td>{sh.y.toFixed(3)}</td>
              <td>{sh.z.toFixed(3)}</td>
              <td>{sh.angle_deg == null ? "—" : `${sh.angle_deg.toFixed(1)}°`}</td>
              <td>{sh.radial_mm == null ? "—" : sh.radial_mm.toFixed(2)}</td>
              <td>{sh.deviation_mm == null ? "—" : `${sh.deviation_mm >= 0 ? "+" : ""}${sh.deviation_mm.toFixed(2)}`}</td>
              <td>{sh.z_residual_mm == null ? "—" : `${sh.z_residual_mm >= 0 ? "+" : ""}${sh.z_residual_mm.toFixed(2)}`}</td>
              <td className="left">{sh.within_tol === false ? "OUT OF TOL" : sh.within_tol ? "pass" : "—"}</td>
            </tr>
          ))}
        </tbody>
      </table>

      {refs.length > 0 && (
        <>
          <h2>Reference ring shots (pipe-CL derivation)</h2>
          <table className="fx-shots">
            <thead>
              <tr>
                <th className="left">Ref #</th>
                <th className="left">Label</th>
                <th>X (m)</th>
                <th>Y (m)</th>
                <th>Z (m)</th>
              </tr>
            </thead>
            <tbody>
              {refs.map((sh, i) => (
                <tr key={sh.id}>
                  <td className="left">R{i + 1}</td>
                  <td className="left">{sh.label || "—"}</td>
                  <td>{sh.x.toFixed(3)}</td>
                  <td>{sh.y.toFixed(3)}</td>
                  <td>{sh.z.toFixed(3)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}

      {/* -------- Notes -------- */}
      <h2>Notes &amp; QA</h2>
      <div className="fx-card mute" style={{ fontSize: "8.5pt", lineHeight: 1.5 }}>
        <p style={{ margin: 0 }}>
          <b>Method.</b> Best-fit circle by Kåsa least-squares on the primary
          ring. Flatness plane fitted independently by orthogonal
          least-squares. Deviations are computed in the fitted-plane frame
          — Δr = radial minus fitted radius, Δz = out-of-plane residual.
        </p>
        <p style={{ marginTop: "2mm", marginBottom: 0 }}>
          <b>Correction.</b> All coordinates already include the axial
          prism constant applied by the TS16 (BAP_SetTargetType). Any
          perpendicular ball offset ({(activeTarget?.perpendicular_mm ?? 0).toFixed(2)} mm)
          is applied post-shot along the fitted surface normal.
        </p>
        <p style={{ marginTop: "2mm", marginBottom: 0 }}>
          <b>Instrument.</b> Leica TS16 (0.5&Prime;), controlled wirelessly
          via FabAxis over Bluetooth SPP + GeoCOM. Live station-state
          telemetry embedded in every CSV export.
        </p>
      </div>

      {/* -------- Footer chrome (repeats on every page) -------- */}
      <div className="fx-footer">
        <span>FabAxis · Wilson Design</span>
        <span>Ovality &amp; Flatness · {s.name}</span>
        <span>{now.toLocaleDateString()}</span>
      </div>
    </div>
  );
};


// ---------- Main panel -----------------------------------------------

export default function OvalityCheck({ onBack }) {
  const { user } = useAuth();
  const [sessions, setSessions] = useState([]);
  const [current, setCurrent] = useState(null);
  const [newName, setNewName] = useState("");
  const [newTol, setNewTol] = useState("5.0");
  const [newDia, setNewDia] = useState("");
  const [tolInput, setTolInput] = useState("5.0");
  const [diaInput, setDiaInput] = useState("");
  const [labelInput, setLabelInput] = useState("");
  const [measuring, setMeasuring] = useState(false);
  const [targetSetting, setTargetSetting] = useState(null);
  // "primary" = shoot the open-end (circle we're checking).
  // "reference" = shoot the second ring further down the pipe, used to
  //               derive the pipe centreline. New shots land in the
  //               reference bucket while this is active.
  const [shotMode, setShotMode] = useState("primary");
  // Flatness panel display mode:
  //   "fit" = residuals vs the best-fit plane through the open-end shots.
  //   "cl"  = residuals vs the plane perpendicular to the pipe axis.
  // Auto-flips to "cl" once the pipe axis is derived; user can switch back.
  const [planeMode, setPlaneMode] = useState("fit");
  // Photo overlay — user snaps a picture of the flange face and drops
  // the coloured deviation dots on top of it, so the report ties a
  // mm value directly to a spot the surveyor's eye can find. State
  // is persisted to localStorage keyed by session id so it survives
  // reloads and browser tab switches on the tablet.
  const [photoUrl, setPhotoUrl] = useState(null);
  const [photoT, setPhotoT] = useState({ x: 0, y: 0, scale: 1, rot: 0 });
  // Photo anchors: {shot_id: [px, py]} — where each shot lands ON the
  // photo. Once 3+ are set the overlay auto-solves an affine transform
  // (or a full homography with 4+) that maps mm-in-plane → pixels-on-
  // photo, so the remaining dots snap to their correct visual place
  // even if the photo isn't shot square-on to the flange face.
  const [photoAnchors, setPhotoAnchors] = useState({});
  // Which shot is the user currently placing on the photo? null = idle
  const [pickShotId, setPickShotId] = useState(null);
  const photoFileRef = React.useRef(null);

  // Live presence — supervisor watch page picks up any active Ovality
  // session so a foreman on the office laptop can see shots land in
  // real time. Heartbeats only while a session is open.
  useLivePresence({
    enabled: !!current?.id,
    tool: "ovality",
    sessionId: current?.id,
    sessionName: current?.name,
    username: user?.username,
    role: user?.role,
    counters: {
      shots: current?.shots?.length || 0,
      refs: current?.reference_shots?.length || 0,
      ovality_mm: current?.ovality_mm ?? null,
      max_dev_mm: current?.max_radial_dev_mm ?? null,
    },
  });

  // Load the saved photo + transform + anchors when session changes.
  useEffect(() => {
    if (!current?.id) {
      setPhotoUrl(null); setPhotoT({ x: 0, y: 0, scale: 1, rot: 0 });
      setPhotoAnchors({}); setPickShotId(null);
      return;
    }
    try {
      const raw = localStorage.getItem(`oval-photo:${current.id}`);
      if (raw) {
        const parsed = JSON.parse(raw);
        setPhotoUrl(parsed.url || null);
        setPhotoT(parsed.t || { x: 0, y: 0, scale: 1, rot: 0 });
        setPhotoAnchors(parsed.anchors || {});
      } else {
        setPhotoUrl(null); setPhotoAnchors({});
        setPhotoT({ x: 0, y: 0, scale: 1, rot: 0 });
      }
      setPickShotId(null);
    } catch {
      setPhotoUrl(null); setPhotoAnchors({});
    }
  }, [current?.id]);

  // Persist photo + transform + anchors.
  useEffect(() => {
    if (!current?.id) return;
    if (!photoUrl) {
      localStorage.removeItem(`oval-photo:${current.id}`);
      return;
    }
    try {
      localStorage.setItem(
        `oval-photo:${current.id}`,
        JSON.stringify({ url: photoUrl, t: photoT, anchors: photoAnchors })
      );
    } catch { /* quota — silently drop */ }
  }, [current?.id, photoUrl, photoT, photoAnchors]);

  const attachPhoto = (file) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      setPhotoUrl(reader.result);
      setPhotoT({ x: 0, y: 0, scale: 1, rot: 0 });
      setPhotoAnchors({});
      setPickShotId(null);
      toast.success("Photo attached — pick a shot then tap where it is on the flange");
    };
    reader.readAsDataURL(file);
  };

  const refreshSessions = useCallback(async () => {
    try {
      const list = await ovalApi.listSessions();
      setSessions(list);
    } catch {
      toast.error("Could not load sessions");
    }
  }, []);

  const refreshCurrent = useCallback(async (sid) => {
    try {
      const s = await ovalApi.get(sid);
      setCurrent(s);
      setTolInput(String(s.tolerance_mm));
      setDiaInput(s.target_diameter_mm ? String(s.target_diameter_mm) : "");
    } catch {
      setCurrent(null);
    }
  }, []);

  useEffect(() => {
    const load = async () => {
      try {
        const [s, ps] = await Promise.all([targetApi.get(), targetApi.presets()]);
        const active = s.preset_id === "custom"
          ? s.custom
          : ps.find((p) => p.id === s.preset_id) || ps[0];
        setTargetSetting(active);
      } catch { /* offline */ }
    };
    load();
    const id = setInterval(load, 2000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => { refreshSessions(); }, [refreshSessions]);

  // Auto-flip the flatness display to the pipe-CL plane the moment the
  // pipe axis becomes derivable. The user can still toggle back to the
  // best-fit view using the header switch.
  useEffect(() => {
    if (current?.pipe_axis_ready) {
      setPlaneMode((m) => (m === "fit" ? "cl" : m));
    }
  }, [current?.pipe_axis_ready]);

  const createSession = async () => {
    if (!newName.trim()) { toast.error("Give the section a name"); return; }
    const tol = parseFloat(newTol);
    if (!Number.isFinite(tol) || tol <= 0) { toast.error("Tolerance must be a positive number of mm"); return; }
    const diaRaw = newDia.trim();
    const dia = diaRaw ? parseFloat(diaRaw) : null;
    if (diaRaw && (!Number.isFinite(dia) || dia <= 0)) { toast.error("Nominal diameter must be a positive number of mm"); return; }
    try {
      const s = await ovalApi.create({
        name: newName.trim(),
        tolerance_mm: tol,
        target_diameter_mm: dia,
      });
      setNewName("");
      setNewDia("");
      await refreshSessions();
      await refreshCurrent(s.id);
      toast.success("Session ready — take your first shot on the section");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to create session");
    }
  };

  // Persist a measurement into the current session — used by both the
  // legacy Reflectorless button path (via takeShot below) and the new
  // ShootPanel component which fires the measure itself and hands us
  // the result. Extracted so both entry-points share the same payload
  // build / toast messaging / session refresh.
  const persistShot = async (result) => {
    if (!current) return;
    if (result?.x == null || result?.y == null || result?.z == null) {
      toast.error("No coord returned — is the TS16 aimed at the target?");
      return;
    }
    setMeasuring(true);
    try {
      const payload = {
        label: labelInput.trim() || null,
        x: result.x, y: result.y, z: result.z,
        hz: result.hz, v: result.v, sd: result.sd,
        target_preset: targetSetting?.label || null,
        target_perpendicular_mm: targetSetting?.perpendicular_mm || 0,
      };
      const updated = shotMode === "reference"
        ? await ovalApi.addReferenceShot(current.id, payload)
        : await ovalApi.addShot(current.id, payload);
      setCurrent(updated);
      setLabelInput("");
      refreshSessions();
      if (shotMode === "reference") {
        const n = updated.reference_shots.length;
        if (!updated.reference_fit_ready) {
          const need = Math.max(0, 3 - n);
          toast.success(`Reference shot #${n} logged — need ${need} more to fit`);
        } else if (updated.pipe_axis_ready) {
          toast.success(
            `Pipe axis derived · square to CL ${updated.squareness_deg?.toFixed(2)}°`
          );
        } else {
          toast.success(`Reference circle fit (${n} shots)`);
        }
        return;
      }
      const n = updated.shots.length;
      if (!updated.fit_ready) {
        if (n < 3) toast.success(`Shot #${n} logged — need ${3 - n} more to fit`);
        else toast.success(`Shot #${n} logged`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || "Failed to log shot");
    } finally { setMeasuring(false); }
  };

  const takeShot = async () => {
    if (!current) return;
    setMeasuring(true);
    try {
      const result = await station.measure("coord");
      await persistShot(result);
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || "Measure failed");
    } finally { setMeasuring(false); }
  };
  const _takeShot_legacy_body_start = null;  // Removed — see persistShot + takeShot above.

  const deleteShot = async (shot_id) => {
    if (!current) return;
    try {
      const s = await ovalApi.removeShot(current.id, shot_id);
      setCurrent(s);
      refreshSessions();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  const deleteReferenceShot = async (shot_id) => {
    if (!current) return;
    try {
      const s = await ovalApi.removeReferenceShot(current.id, shot_id);
      setCurrent(s);
      refreshSessions();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  const deleteSession = async (sid) => {
    if (!window.confirm("Delete this ovality session?")) return;
    try {
      await ovalApi.remove(sid);
      if (current?.id === sid) setCurrent(null);
      refreshSessions();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  const saveTolerance = async () => {
    if (!current) return;
    const t = parseFloat(tolInput);
    if (!Number.isFinite(t) || t <= 0) return;
    try {
      const s = await ovalApi.setTolerance(current.id, t);
      setCurrent(s);
      refreshSessions();
      toast.success(`Tolerance set to ±${t.toFixed(1)} mm`);
    } catch {
      toast.error("Could not update tolerance");
    }
  };

  const saveTargetDiameter = async () => {
    if (!current) return;
    const raw = diaInput.trim();
    const d = raw ? parseFloat(raw) : null;
    if (raw && (!Number.isFinite(d) || d <= 0)) { toast.error("Diameter must be positive"); return; }
    try {
      const s = await ovalApi.setTargetDiameter(current.id, d);
      setCurrent(s);
      refreshSessions();
      toast.success(d ? `Nominal Ø ${d.toFixed(1)} mm` : "Nominal cleared");
    } catch {
      toast.error("Could not update diameter");
    }
  };

  // ---------- List view ----------

  if (!current) {
    return (
      <div className="mx-auto max-w-3xl p-6" data-testid="oval-page-list">
        <div className="mb-4 flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#8B939A]">Ovality &amp; Flatness</div>
            <h2 className="text-2xl font-bold uppercase tracking-tight text-white" style={{ fontFamily: "Chakra Petch, sans-serif" }}>
              Round-ness &amp; plane fit
            </h2>
          </div>
          <button
            data-testid="oval-back-list"
            onClick={onBack}
            className="rounded-md border border-[#2A2E39] bg-[#14161A] px-3 py-1.5 text-[10px] uppercase tracking-widest text-[#8B939A] hover:border-[#009E8E] hover:text-white"
          >
            ← Toolbox
          </button>
        </div>

        {/* New session card */}
        <div className="mb-6 rounded-md border border-[#2A2E39] bg-gradient-to-br from-[#1A1D24] to-[#14161A] p-4">
          <div className="mb-3 text-[10px] uppercase tracking-widest text-[#8B939A]">New session</div>
          <div className="grid gap-3 sm:grid-cols-[1fr,120px,140px]">
            <input
              data-testid="oval-new-name"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              placeholder="Section ID (e.g. Chimney @ +12m)"
              className="rounded-sm border border-[#2A2E39] bg-[#0A0B0E] px-3 py-2 font-mono text-sm text-white placeholder:text-[#4A5054] focus:border-[#009E8E] focus:outline-none"
            />
            <div className="flex items-center gap-2">
              <input
                data-testid="oval-new-tol"
                type="number"
                step="0.1"
                value={newTol}
                onChange={(e) => setNewTol(e.target.value)}
                className="w-full rounded-sm border border-[#2A2E39] bg-[#0A0B0E] px-3 py-2 font-mono text-sm text-white focus:border-[#009E8E] focus:outline-none"
              />
              <span className="text-[10px] uppercase tracking-widest text-[#8B939A]">± mm</span>
            </div>
            <button
              data-testid="oval-create-btn"
              onClick={createSession}
              className="flex items-center justify-center gap-2 rounded-sm border border-[#009E8E] bg-[#009E8E]/10 px-3 py-2 text-[11px] font-bold uppercase tracking-widest text-[#009E8E] hover:bg-[#009E8E]/20"
              style={{ fontFamily: "Chakra Petch, sans-serif" }}
            >
              <Plus size={12} weight="bold" /> Create
            </button>
          </div>
          <div className="mt-3 grid gap-3 sm:grid-cols-[1fr,140px]">
            <input
              data-testid="oval-new-diameter"
              type="number"
              step="1"
              value={newDia}
              onChange={(e) => setNewDia(e.target.value)}
              placeholder="Optional nominal diameter (mm)"
              className="rounded-sm border border-[#2A2E39] bg-[#0A0B0E] px-3 py-2 font-mono text-sm text-white placeholder:text-[#4A5054] focus:border-[#009E8E] focus:outline-none"
            />
            <div className="text-[10px] text-[#5A6065]" style={{ fontFamily: "JetBrains Mono, monospace" }}>
              For spec-vs-actual comparison
            </div>
          </div>
          <div className="mt-2 text-[10px] text-[#5A6065]" style={{ fontFamily: "JetBrains Mono, monospace" }}>
            API 650 tanks: ±1% of D. Chimneys: ±0.5% or spec. Pipe: fabricator spec. Take 6–12 shots for a reliable read.
          </div>
        </div>

        <div className="mb-2 text-[10px] uppercase tracking-widest text-[#8B939A]">Sessions</div>
        {sessions.length === 0 ? (
          <div className="rounded-md border border-dashed border-[#2A2E39] p-6 text-center text-[11px] text-[#5A6065]">
            No sessions yet. Create one above to start.
          </div>
        ) : (
          <div className="space-y-2">
            {sessions.map((s) => (
              <SessionCard
                key={s.id}
                session={s}
                onOpen={refreshCurrent}
                onDelete={deleteSession}
              />
            ))}
          </div>
        )}
      </div>
    );
  }

  // ---------- Session view ----------

  const shots = current.shots || [];
  const headline = current.fit_ready
    ? `${shots.length} shots · Ø ${(current.radius_mm * 2).toFixed(1)} mm · ovality ${current.ovality_mm.toFixed(1)} mm`
    : `${shots.length}/3 shots · fit not ready`;

  return (
    <ToolSplitLayout
      left={
    <div className="mx-auto max-w-5xl" data-testid="oval-page-session">
      {/* Print stylesheet — turns the on-screen tool page into an A4
          field report. The ".no-print" chrome disappears, the
          ".print-only" report block takes over, and the whole document
          flips to light-theme so it's legible on ink. */}
      <style>{`
        @media print {
          @page {
            size: A4;
            /* Bottom margin must be big enough to contain the fixed
               .fx-footer AND leave a visible gap between it and the
               last line of body content. Footer is ~7-9mm tall
               (border + padding + one line at 7.5pt + font fallback
               slack) sitting at bottom:6mm; keep 24mm reserved so
               content never bleeds underneath it. */
            margin: 14mm 12mm 24mm 12mm;
          }
          html, body {
            background: #ffffff !important;
            color: #0A0B0E !important;
          }
          .no-print { display: none !important; }
          .print-only { display: block !important; }
          /* Kill the split layout on print — report needs the full width. */
          [data-testid="tool-split-layout"] {
            display: block !important;
            height: auto !important;
            overflow: visible !important;
            background: #ffffff !important;
          }
          [data-testid="tool-split-left"] {
            overflow: visible !important;
            padding: 0 !important;
          }
          [data-testid="tool-split-right"] { display: none !important; }
          [data-testid="oval-page-session"] {
            padding: 0 !important;
            max-width: none !important;
          }
          /* Hide EVERY on-screen child of the session view except the
             print-only report block. Prior bug: the ToolSplitLayout's
             left column (title + fit-summary + SectionView +
             PlaneView + shot table) rendered a SECOND time under the
             print-only report, producing duplicate pages 3-4. */
          [data-testid="oval-page-session"] > *:not(.print-only):not(style) {
            display: none !important;
          }
          /* Report typography */
          .fx-report {
            font-family: "Helvetica Neue", Helvetica, Arial, sans-serif;
            color: #0A0B0E;
            font-size: 10pt;
            line-height: 1.35;
          }
          .fx-report .fx-h1 {
            font-family: "Chakra Petch", "Helvetica Neue", sans-serif;
            font-weight: 700;
            letter-spacing: 0.02em;
            text-transform: uppercase;
          }
          .fx-report .fx-hairline { border-top: 0.5pt solid #0A0B0E; }
          .fx-report .fx-thick    { border-top: 2pt   solid #B0000A; }
          .fx-report .fx-card {
            border: 0.5pt solid #333;
            border-radius: 2mm;
            padding: 3mm 4mm;
            background: #FFFFFF;
          }
          .fx-report .fx-card.mute {
            background: #F5F5F3;
            border: 0.5pt solid #C8C8C4;
          }
          .fx-report .fx-mono {
            font-family: "JetBrains Mono", "Courier New", monospace;
          }
          .fx-report .fx-tint-pass { background: #E9F4E2; color: #375A1D; }
          .fx-report .fx-tint-fail { background: #FCE6E6; color: #7A1414; }
          .fx-report .fx-avoid-break { page-break-inside: avoid; break-inside: avoid; }
          .fx-report h2 {
            font-family: "Chakra Petch", "Helvetica Neue", sans-serif;
            font-weight: 700;
            text-transform: uppercase;
            letter-spacing: 0.1em;
            font-size: 10pt;
            color: #0A0B0E;
            border-bottom: 0.5pt solid #B0000A;
            padding-bottom: 1mm;
            margin: 6mm 0 3mm;
          }
          .fx-report table.fx-shots {
            width: 100%;
            border-collapse: collapse;
            font-family: "JetBrains Mono", "Courier New", monospace;
            font-size: 8.5pt;
          }
          .fx-report table.fx-shots th {
            background: #EEE;
            border-bottom: 0.5pt solid #333;
            padding: 1.5mm 2mm;
            text-align: right;
            font-weight: 700;
            text-transform: uppercase;
            letter-spacing: 0.08em;
            font-size: 7.5pt;
          }
          .fx-report table.fx-shots th:first-child,
          .fx-report table.fx-shots td:first-child,
          .fx-report table.fx-shots th.left,
          .fx-report table.fx-shots td.left { text-align: left; }
          .fx-report table.fx-shots td {
            padding: 1.5mm 2mm;
            border-bottom: 0.25pt solid #CCCCC8;
          }
          .fx-report table.fx-shots tr.fail td { color: #7A1414; }
          .fx-report .fx-chart-frame {
            border: 0.5pt solid #333;
            border-radius: 2mm;
            padding: 2mm;
            background: #FFFFFF;
          }
          .fx-report .fx-chart-frame svg {
            width: 100%;
            height: auto;
            max-height: 90mm;
          }
          /* Force the on-screen dark theme colours inside charts to
             print-friendly ones. Swap dark backgrounds → white and
             faint grey grid lines → medium grey so they show up. */
          .fx-report .fx-chart-frame rect[fill="#14161A"],
          .fx-report .fx-chart-frame rect[fill="#0A0B0E"] { fill: #FFFFFF !important; }
          .fx-report .fx-chart-frame line[stroke="#1A1D24"],
          .fx-report .fx-chart-frame line[stroke="#2A2E39"] { stroke: #C8C8C8 !important; }
          .fx-report .fx-chart-frame text { fill: #333 !important; }
          .fx-report .fx-footer {
            position: fixed;
            bottom: 6mm;
            left: 12mm;
            right: 12mm;
            border-top: 0.5pt solid #B0000A;
            padding-top: 1.5mm;
            font-family: "Chakra Petch", "Helvetica Neue", sans-serif;
            font-size: 7.5pt;
            line-height: 1.2;
            color: #555;
            letter-spacing: 0.15em;
            text-transform: uppercase;
            display: flex;
            justify-content: space-between;
            /* Belt-and-braces: opaque background so any content that
               did drift into the bottom-margin zone (long tables that
               force a page break mid-row, etc) can't visually bleed
               through the footer. */
            background: #FFFFFF;
          }
          /* Give the report body some breathing room before the fixed
             footer. Chrome pagination sometimes places a card exactly
             at the page-margin edge; this pad guarantees a visible gap. */
          .fx-report { padding-bottom: 4mm; }
        }
        .print-only { display: none; }
      `}</style>

      {/* --------------- PRINT-ONLY REPORT (A4) -------------------- */}
      <div className="print-only fx-report">
        <PrintReport session={current} user={user} activeTarget={targetSetting} />
      </div>
      {/* ----------------------------------------------------------- */}

      <div className="mb-4 flex items-center justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-[#8B939A]">Ovality &amp; Flatness · {current.name}</div>
          <h2 className="text-2xl font-bold uppercase tracking-tight text-white" style={{ fontFamily: "Chakra Petch, sans-serif" }}>
            {headline}
          </h2>
        </div>
        <input
          ref={photoFileRef}
          type="file"
          accept="image/*"
          capture="environment"
          style={{ display: "none" }}
          onChange={(e) => attachPhoto(e.target.files?.[0])}
          data-testid="oval-photo-input"
        />
      </div>

      {/* Fit summary */}
      {current.fit_ready && (
        <div className="mb-4 grid grid-cols-2 gap-3 rounded-md border border-[#009E8E]/30 bg-[#009E8E]/5 p-3 md:grid-cols-4">
          <Stat label="Fitted Ø" value={`${(current.radius_mm * 2).toFixed(1)} mm`} />
          {(() => {
            // Compute min/max as-built diameters at 0/45/90/135° for the
            // summary card — mirrors what the section-view chords show.
            const sorted = (current.shots || [])
              .filter((s) => s.angle_deg != null && s.radial_mm != null)
              .sort((a, b) => a.angle_deg - b.angle_deg);
            if (sorted.length < 3) return null;
            const interp = (targetDeg) => {
              const norm = ((targetDeg % 360) + 360) % 360;
              for (let k = 0; k < sorted.length; k++) {
                const a1 = sorted[k].angle_deg;
                const a2 = sorted[(k + 1) % sorted.length].angle_deg;
                let span = a2 - a1;
                if (span <= 0) span += 360;
                let offset = norm - a1;
                if (offset < 0) offset += 360;
                if (offset <= span + 1e-6) {
                  const t = span > 0 ? offset / span : 0;
                  return sorted[k].radial_mm + t * (sorted[(k + 1) % sorted.length].radial_mm - sorted[k].radial_mm);
                }
              }
              return sorted[0].radial_mm;
            };
            const ds = [0, 45, 90, 135].map((deg) => ({
              deg,
              d: interp(deg) + interp(deg + 180),
            }));
            const mn = ds.reduce((a, b) => (a.d < b.d ? a : b));
            const mx = ds.reduce((a, b) => (a.d > b.d ? a : b));
            return (
              <>
                <Stat
                  label={`Min Ø @ ${mn.deg}°`}
                  value={`${mn.d.toFixed(1)} mm`}
                  tone="warn"
                />
                <Stat
                  label={`Max Ø @ ${mx.deg}°`}
                  value={`${mx.d.toFixed(1)} mm`}
                  tone="fail"
                />
              </>
            );
          })()}
          <Stat
            label="Ovality (D max − D min)"
            value={`${current.ovality_mm.toFixed(1)} mm`}
            tone={current.within_tol ? "ok" : "fail"}
          />
          <Stat label="Max radial dev" value={`${current.max_radial_dev_mm.toFixed(1)} mm`} tone={current.within_tol ? "ok" : "fail"} />
          <Stat label="RMS dev" value={`${current.rms_dev_mm.toFixed(2)} mm`} />
          {current.plane_ready && (
            <Stat
              label="As-built tilt"
              value={
                current.plane_orientation === "horizontal"
                  ? `${current.tilt_total_deg.toFixed(3)}° · ${Math.hypot(current.ew_slope_mm_per_m || 0, current.ns_slope_mm_per_m || 0).toFixed(1)} mm/m`
                  : current.plane_orientation === "vertical"
                    ? `Vertical · faces ${current.plane_normal_bearing_deg != null ? `${current.plane_normal_bearing_deg.toFixed(0)}°` : "—"}`
                    : `${current.tilt_total_deg.toFixed(2)}° tilted`
              }
              tone={
                (current.plane_max_residual_mm || 0) <= current.tolerance_mm
                  ? "ok"
                  : (current.plane_max_residual_mm || 0) <= current.tolerance_mm * 2
                    ? "warn"
                    : "fail"
              }
            />
          )}
          {current.target_diameter_mm && (
            <Stat
              label="Ø offset (fitted − nominal)"
              value={`${current.diameter_offset_mm > 0 ? "+" : ""}${current.diameter_offset_mm.toFixed(1)} mm`}
              tone={Math.abs(current.diameter_offset_mm) <= current.tolerance_mm * 2 ? "ok" : "warn"}
            />
          )}
        </div>
      )}

      {/* Pipe centreline summary — appears once the reference ring has
          been fit and a pipe axis derived. Squareness is the killer
          number here: 0° means the flange face is perpendicular to the
          pipe centreline. */}
      {current.pipe_axis_ready && (
        <div className="mb-4 grid grid-cols-2 gap-3 rounded-md border border-[#F5A623]/30 bg-[#F5A623]/5 p-3 md:grid-cols-4" data-testid="oval-pipe-axis-summary">
          <Stat
            label="Squareness to CL"
            value={`${current.squareness_deg.toFixed(2)}°`}
            tone={current.squareness_deg <= 0.5 ? "ok" : current.squareness_deg <= 2 ? "warn" : "fail"}
          />
          <Stat
            label="CL offset (concentricity)"
            value={`${current.cl_offset_mm.toFixed(1)} mm`}
            tone={current.cl_offset_mm <= current.tolerance_mm ? "ok" : current.cl_offset_mm <= current.tolerance_mm * 2 ? "warn" : "fail"}
          />
          <Stat
            label="Ring separation"
            value={`${current.ring_separation_mm.toFixed(0)} mm`}
          />
          <Stat
            label="Pipe axis"
            value={
              current.pipe_bearing_deg != null
                ? `${current.pipe_bearing_deg.toFixed(0)}° · ${current.pipe_inclination_deg.toFixed(1)}° incl`
                : `${current.pipe_inclination_deg.toFixed(1)}° incl`
            }
          />
        </div>
      )}

      {/* Visualisations */}
      <div className="mb-4 grid gap-4 md:grid-cols-2">
        <SectionView session={current} />
        <PlaneView
          session={current}
          planeMode={planeMode}
          onPlaneModeChange={setPlaneMode}
        />
      </div>

      {/* Photo overlay — appears once the user has attached a picture
          of the flange. The deviation dots from the section view are
          drawn over the photo with an X/Y/scale/rotation transform the
          user can nudge to line up with what they see in the image.
          Prints as part of the PDF report. */}
      {photoUrl && current.fit_ready && (
        <PhotoOverlay
          session={current}
          photoUrl={photoUrl}
          transform={photoT}
          onTransformChange={setPhotoT}
          anchors={photoAnchors}
          onAnchorsChange={setPhotoAnchors}
          pickShotId={pickShotId}
          onPickShotId={setPickShotId}
          onClear={() => { setPhotoUrl(null); setPhotoAnchors({}); setPickShotId(null); }}
        />
      )}

      {/* Shot table */}
      <div className="overflow-hidden rounded-md border border-[#2A2E39] bg-[#14161A]">
        <div className="border-b border-[#2A2E39] bg-[#0A0B0E] px-2 py-1.5 text-[9px] uppercase tracking-widest text-[#009E8E]" style={{ fontFamily: "Chakra Petch, sans-serif" }}>
          Primary shots — open-end / flange circle
        </div>
        <table className="w-full text-[11px]" style={{ fontFamily: "JetBrains Mono, monospace" }}>
          <thead>
            <tr className="border-b border-[#2A2E39] bg-[#0A0B0E] text-[#8B939A]">
              <th className="p-2 text-left uppercase tracking-widest">#</th>
              <th className="p-2 text-left uppercase tracking-widest">Label</th>
              <th className="p-2 text-right uppercase tracking-widest">Angle</th>
              <th className="p-2 text-right uppercase tracking-widest">Radial (mm)</th>
              <th className="p-2 text-right uppercase tracking-widest">Dev (mm)</th>
              <th className="p-2 text-right uppercase tracking-widest">Z resid (mm)</th>
              <th className="p-2 text-center uppercase tracking-widest">Status</th>
              <th className="p-2 uppercase tracking-widest"></th>
            </tr>
          </thead>
          <tbody className="text-[#B5BEC5]">
            {shots.length === 0 ? (
              <tr>
                <td colSpan={8} className="p-6 text-center text-[#5A6065]">
                  No shots yet — hit &quot;Take shot #1&quot; to begin.
                </td>
              </tr>
            ) : (
              shots.map((s, i) => (
                <ShotRow key={s.id} shot={s} index={i} tol={current.tolerance_mm} onDelete={deleteShot} />
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Reference-ring shots — collapsible, only render when there is
          at least one reference shot OR the surveyor is actively in
          reference-shot mode (so they can see the empty state). */}
      {(shotMode === "reference" || (current.reference_shots || []).length > 0) && (
        <div className="mt-3 overflow-hidden rounded-md border border-[#F5A623]/30 bg-[#14161A]" data-testid="oval-reference-table">
          <div className="flex items-center justify-between border-b border-[#2A2E39] bg-[#0A0B0E] px-2 py-1.5 text-[9px] uppercase tracking-widest" style={{ fontFamily: "Chakra Petch, sans-serif" }}>
            <span className="text-[#F5A623]">Reference ring — pipe-CL derivation</span>
            <span className="text-[#5A6065]" style={{ fontFamily: "JetBrains Mono, monospace" }}>
              {current.reference_fit_ready
                ? `Ø ${((current.reference_radius_mm || 0) * 2).toFixed(0)} mm · fit ok`
                : `${(current.reference_shots || []).length}/3 shots · needs 3+ for a fit`}
            </span>
          </div>
          <table className="w-full text-[11px]" style={{ fontFamily: "JetBrains Mono, monospace" }}>
            <thead>
              <tr className="border-b border-[#2A2E39] bg-[#0A0B0E] text-[#8B939A]">
                <th className="p-2 text-left uppercase tracking-widest">#</th>
                <th className="p-2 text-left uppercase tracking-widest">Label</th>
                <th className="p-2 text-right uppercase tracking-widest">X (m)</th>
                <th className="p-2 text-right uppercase tracking-widest">Y (m)</th>
                <th className="p-2 text-right uppercase tracking-widest">Z (m)</th>
                <th className="p-2 uppercase tracking-widest"></th>
              </tr>
            </thead>
            <tbody className="text-[#B5BEC5]">
              {(current.reference_shots || []).length === 0 ? (
                <tr>
                  <td colSpan={6} className="p-4 text-center text-[#5A6065]">
                    No reference shots yet — take 3+ shots around a ring further down the pipe.
                  </td>
                </tr>
              ) : (
                (current.reference_shots || []).map((s, i) => (
                  <tr key={s.id} data-testid={`oval-ref-shot-${s.id}`} className="border-b border-[#1A1D24]">
                    <td className="p-2 text-white">R{i + 1}</td>
                    <td className="p-2 text-white">{s.label || "—"}</td>
                    <td className="p-2 text-right">{fmt(s.x, 3)}</td>
                    <td className="p-2 text-right">{fmt(s.y, 3)}</td>
                    <td className="p-2 text-right">{fmt(s.z, 3)}</td>
                    <td className="p-2 text-right">
                      <button
                        data-testid={`oval-ref-shot-delete-${s.id}`}
                        onClick={() => deleteReferenceShot(s.id)}
                        className="rounded-sm border border-transparent p-1 text-[#5A6065] hover:border-[#FF3B30] hover:text-[#FF3B30]"
                        title="Delete reference shot"
                      >
                        <X size={12} weight="bold" />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
      }
      right={
        <>
          {/* Session nav row */}
          <div className="flex items-center gap-2">
            <button
              data-testid="oval-back-session"
              onClick={() => setCurrent(null)}
              className="flex-1 rounded-md border border-[#2A2E39] bg-[#14161A] px-3 py-2 text-[10px] uppercase tracking-widest text-[#8B939A] hover:border-[#009E8E] hover:text-white"
            >
              ← Sessions
            </button>
            <a
              data-testid="oval-download-csv"
              href={ovalApi.csvUrl(current.id)}
              className="flex items-center gap-1 rounded-md border border-[#2A2E39] bg-[#14161A] px-2 py-2 text-[10px] uppercase tracking-widest text-[#8B939A] hover:border-[#009E8E] hover:text-white"
              title="Download CSV report"
            >
              <Download size={12} weight="bold" /> CSV
            </a>
            <button
              data-testid="oval-print-pdf"
              onClick={() => window.print()}
              className="flex items-center gap-1 rounded-md border border-[#009E8E] bg-[#009E8E]/10 px-2 py-2 text-[10px] uppercase tracking-widest text-[#009E8E] hover:bg-[#009E8E]/20"
              title="Print or save as PDF"
            >
              <Download size={12} weight="bold" /> PDF
            </button>
          </div>

          <button
            data-testid="oval-attach-photo"
            onClick={() => photoFileRef.current?.click()}
            className="flex items-center justify-center gap-1 rounded-md border border-[#2A2E39] bg-[#14161A] px-3 py-2 text-[10px] uppercase tracking-widest text-[#8B939A] hover:border-[#009E8E] hover:text-white"
            title="Snap or pick a photo of the flange"
          >
            <Camera size={13} weight="bold" /> Attach flange photo
          </button>

          {/* Universal shoot control — Reflectorless button or Prism
              auto-shoot panel depending on the global Prism-mode
              toggle in the header. Fires persistShot() with whatever
              measurement was captured. */}
          <ShootPanel
            data-testid="oval-shoot-panel"
            onShot={persistShot}
            disabled={measuring}
            label={
              shotMode === "reference"
                ? `Ref shot #${(current.reference_shots || []).length + 1}`
                : `Take shot #${shots.length + 1}`
            }
          />

          <AimPad tool="ovality" sessionId={current.id} />
          <div>
            <div className="mb-1 text-[10px] uppercase tracking-widest text-[#8B939A]">
              Shot target
            </div>
            <div className="grid grid-cols-2 overflow-hidden rounded-sm border border-[#2A2E39] bg-[#14161A]" data-testid="oval-shot-mode">
              <button
                data-testid="oval-mode-primary"
                onClick={() => setShotMode("primary")}
                className={`px-2 py-2 text-[10px] font-bold uppercase tracking-widest transition-colors ${
                  shotMode === "primary"
                    ? "bg-[#009E8E]/20 text-[#009E8E]"
                    : "text-[#8B939A] hover:text-white"
                }`}
                style={{ fontFamily: "Chakra Petch, sans-serif" }}
              >
                Primary ({(current.shots || []).length})
              </button>
              <button
                data-testid="oval-mode-reference"
                onClick={() => setShotMode("reference")}
                className={`px-2 py-2 text-[10px] font-bold uppercase tracking-widest transition-colors ${
                  shotMode === "reference"
                    ? "bg-[#F5A623]/20 text-[#F5A623]"
                    : "text-[#8B939A] hover:text-white"
                }`}
                style={{ fontFamily: "Chakra Petch, sans-serif" }}
                title="Take shots on a second circle down the pipe to establish the pipe centreline"
              >
                Reference ({(current.reference_shots || []).length})
              </button>
            </div>
          </div>

          <div className="grid grid-cols-[1fr,88px] gap-2">
            <label className="block">
              <span className="mb-1 block text-[10px] uppercase tracking-widest text-[#8B939A]">
                Shot label
              </span>
              <input
                data-testid="oval-label"
                value={labelInput}
                onChange={(e) => setLabelInput(e.target.value)}
                placeholder="N · NE · E …"
                className="w-full rounded-sm border border-[#2A2E39] bg-[#0A0B0E] px-3 py-2 font-mono text-sm text-white placeholder:text-[#4A5054] focus:border-[#009E8E] focus:outline-none"
              />
            </label>
            <label className="block">
              <span className="mb-1 block text-[10px] uppercase tracking-widest text-[#8B939A]">
                ± Tol mm
              </span>
              <input
                data-testid="oval-tol-input"
                type="number"
                step="0.1"
                value={tolInput}
                onChange={(e) => setTolInput(e.target.value)}
                onBlur={saveTolerance}
                className="w-full rounded-sm border border-[#2A2E39] bg-[#0A0B0E] px-2 py-2 font-mono text-sm text-white focus:border-[#009E8E] focus:outline-none"
              />
            </label>
          </div>

          <label className="block">
            <span className="mb-1 block text-[10px] uppercase tracking-widest text-[#8B939A]">
              Nominal diameter
            </span>
            <div className="flex items-center gap-2">
              <input
                data-testid="oval-diameter-input"
                type="number"
                step="1"
                value={diaInput}
                onChange={(e) => setDiaInput(e.target.value)}
                onBlur={saveTargetDiameter}
                placeholder="Nom. Ø"
                className="w-full rounded-sm border border-[#2A2E39] bg-[#0A0B0E] px-3 py-2 font-mono text-sm text-white placeholder:text-[#4A5054] focus:border-[#009E8E] focus:outline-none"
              />
              <span className="text-[10px] uppercase tracking-widest text-[#8B939A]">Ø mm</span>
            </div>
          </label>

          {current.warning && (
            <div className="rounded-sm border border-[#FF9F0A] bg-[#FF9F0A]/10 px-3 py-2 text-[10px] uppercase tracking-widest text-[#FF9F0A]" style={{ fontFamily: "JetBrains Mono, monospace" }}>
              ⓘ {current.warning}
            </div>
          )}
        </>
      }
    />
  );
}

const Stat = ({ label, value, tone }) => {
  const color = tone === "ok" ? "#8DC63F" : tone === "warn" ? "#FF9F0A" : tone === "fail" ? "#FF3B30" : "#FFFFFF";
  return (
    <div>
      <div className="text-[9px] uppercase tracking-[0.25em] text-[#8B939A]">{label}</div>
      <div className="mt-0.5 font-mono text-base" style={{ color }}>{value}</div>
    </div>
  );
};

// ---------- Photo overlay ----------------------------------------
//
// Wraps a user-supplied image of the flange face and re-projects the
// section view's deviation dots on top. The transform (offset, scale,
// rotation) is user-adjustable via inline sliders so the surveyor can
// line up the pattern with the visible geometry in the photo.
// Auto-fills the container width; height auto-derived from the image
// aspect ratio. Print CSS keeps it visible in the PDF report.

// --- linear-algebra helpers -----------------------------------------
// Gauss elimination with partial pivoting for small dense systems.
// Returns the solution vector, or null if the matrix is singular.
const _solve = (A, b) => {
  const n = A.length;
  const M = A.map((r, i) => [...r, b[i]]);
  for (let i = 0; i < n; i++) {
    let piv = i;
    for (let k = i + 1; k < n; k++) if (Math.abs(M[k][i]) > Math.abs(M[piv][i])) piv = k;
    if (Math.abs(M[piv][i]) < 1e-12) return null;
    [M[i], M[piv]] = [M[piv], M[i]];
    for (let k = i + 1; k < n; k++) {
      const f = M[k][i] / M[i][i];
      for (let j = i; j <= n; j++) M[k][j] -= f * M[i][j];
    }
  }
  const x = new Array(n);
  for (let i = n - 1; i >= 0; i--) {
    let s = M[i][n];
    for (let j = i + 1; j < n; j++) s -= M[i][j] * x[j];
    x[i] = s / M[i][i];
  }
  return x;
};

// Solve a full 3x3 homography from ≥4 (src → dst) point pairs.
// h33 = 1 pinning; over-determined systems solved via normal equations.
const solveHomography = (pairs) => {
  const A = [], b = [];
  for (const { src: [x, y], dst: [X, Y] } of pairs) {
    A.push([x, y, 1, 0, 0, 0, -X * x, -X * y]); b.push(X);
    A.push([0, 0, 0, x, y, 1, -Y * x, -Y * y]); b.push(Y);
  }
  const n = 8;
  const AtA = Array.from({ length: n }, () => new Array(n).fill(0));
  const Atb = new Array(n).fill(0);
  for (let i = 0; i < A.length; i++)
    for (let j = 0; j < n; j++) {
      Atb[j] += A[i][j] * b[i];
      for (let k = 0; k < n; k++) AtA[j][k] += A[i][j] * A[i][k];
    }
  const h = _solve(AtA, Atb);
  return h ? [...h, 1] : null;
};

// 6-parameter affine (3+ point pairs).
const solveAffine = (pairs) => {
  const A = [], b = [];
  for (const { src: [x, y], dst: [X, Y] } of pairs) {
    A.push([x, y, 1, 0, 0, 0]); b.push(X);
    A.push([0, 0, 0, x, y, 1]); b.push(Y);
  }
  const n = 6;
  const AtA = Array.from({ length: n }, () => new Array(n).fill(0));
  const Atb = new Array(n).fill(0);
  for (let i = 0; i < A.length; i++)
    for (let j = 0; j < n; j++) {
      Atb[j] += A[i][j] * b[i];
      for (let k = 0; k < n; k++) AtA[j][k] += A[i][j] * A[i][k];
    }
  return _solve(AtA, Atb);
};

const applyH = (h, x, y) => {
  const w = h[6] * x + h[7] * y + h[8];
  return [(h[0] * x + h[1] * y + h[2]) / w, (h[3] * x + h[4] * y + h[5]) / w];
};
const applyA = (a, x, y) => [a[0] * x + a[1] * y + a[2], a[3] * x + a[4] * y + a[5]];

const PhotoOverlay = ({
  session, photoUrl, transform, onTransformChange,
  anchors, onAnchorsChange, pickShotId, onPickShotId, onClear,
}) => {
  const shots = session.shots || [];
  const tol = session.tolerance_mm || 5;
  const radiusMm = session.radius_mm || 100;

  const pts = shots
    .filter((s) => s.u_m != null && s.v_m != null)
    .map((s, i) => ({
      id: s.id,
      label: s.label || `P${i}`,
      u_mm: (s.u_m - (session.plane_center_u_m || 0)) * 1000,
      v_mm: (s.v_m - (session.plane_center_v_m || 0)) * 1000,
      dev_mm: s.deviation_mm || 0,
    }));

  const colorFor = (mm) => Math.abs(mm) <= tol ? "#8DC63F" : mm > 0 ? "#FF3B30" : "#3B9BFF";

  const W = 800, H = 600;

  // Build (mm-in-plane) → (0..1 photo coord) point pairs from anchors.
  // We flip u to match the section view's operator orientation.
  const pairs = Object.entries(anchors).map(([id, [px, py]]) => {
    const p = pts.find((q) => q.id === id);
    return p ? { src: [-p.u_mm, p.v_mm], dst: [px, py] } : null;
  }).filter(Boolean);

  // Choose the best transform for the anchor count.
  //   ≥4 anchors → full perspective homography
  //    3 anchors → affine (rot + scale + shear + translation)
  //   ≤2 anchors → fallback to manual X/Y/scale/rotation sliders
  let mode = "manual";
  let solvedH = null, solvedA = null;
  if (pairs.length >= 4) { solvedH = solveHomography(pairs); if (solvedH) mode = "homography"; }
  if (mode === "manual" && pairs.length >= 3) { solvedA = solveAffine(pairs); if (solvedA) mode = "affine"; }

  const set = (patch) => onTransformChange({ ...transform, ...patch });

  // Fallback manual projection (2 anchors or fewer).
  const basePxPerMm = (0.4) / (radiusMm * 2);   // photo-normalised units
  const project = (u_mm, v_mm) => {
    if (mode === "homography") {
      const [nx, ny] = applyH(solvedH, -u_mm, v_mm);
      return [nx * W, ny * H];
    }
    if (mode === "affine") {
      const [nx, ny] = applyA(solvedA, -u_mm, v_mm);
      return [nx * W, ny * H];
    }
    // Manual — user's X/Y/scale/rotation sliders (photo-normalised).
    const s = basePxPerMm * transform.scale;
    const rot = (transform.rot * Math.PI) / 180;
    const uu = -u_mm, vv = v_mm;
    const rx = uu * Math.cos(rot) - vv * Math.sin(rot);
    const ry = uu * Math.sin(rot) + vv * Math.cos(rot);
    return [W * (0.5 + transform.x / W) + rx * s * W,
            H * (0.5 - transform.y / H) - ry * s * H];
  };

  // Click handler: places an anchor at the tapped photo coordinate.
  // The picked shot advances to the next un-anchored one so the
  // surveyor can rattle through 4 taps and be done.
  const onPhotoClick = (e) => {
    if (!pickShotId) return;
    const svg = e.currentTarget;
    const rect = svg.getBoundingClientRect();
    const nx = (e.clientX - rect.left) / rect.width;
    const ny = (e.clientY - rect.top) / rect.height;
    onAnchorsChange({ ...anchors, [pickShotId]: [nx, ny] });
    // Auto-advance to the next un-anchored shot.
    const next = pts.find((p) => !(p.id in anchors) && p.id !== pickShotId);
    onPickShotId(next ? next.id : null);
  };

  const clearAnchors = () => { onAnchorsChange({}); onPickShotId(null); };

  return (
    <div className="mb-4 rounded-md border border-[#2A2E39] bg-[#0A0B0E] p-3">
      <div className="mb-2 flex flex-wrap items-center justify-between gap-2 text-[10px] uppercase tracking-widest">
        <span className="text-[#8B939A]" style={{ fontFamily: "Chakra Petch, sans-serif" }}>
          Photo overlay ·{" "}
          {mode === "homography" ? "perspective-solved (4+ anchors)" :
           mode === "affine" ? "affine-solved (3 anchors)" :
           `pick shots then tap the photo (${pairs.length}/3 anchors)`}
        </span>
        <div className="flex items-center gap-2 no-print">
          <button
            data-testid="oval-photo-clear-anchors"
            onClick={clearAnchors}
            className="rounded-sm border border-[#2A2E39] bg-[#14161A] px-2 py-1 text-[9px] uppercase tracking-widest text-[#8B939A] hover:border-[#F5A623] hover:text-[#F5A623]"
          >
            Clear anchors
          </button>
          <button
            data-testid="oval-photo-clear"
            onClick={onClear}
            className="rounded-sm border border-[#2A2E39] bg-[#14161A] px-2 py-1 text-[9px] uppercase tracking-widest text-[#8B939A] hover:border-[#FF3B30] hover:text-[#FF3B30]"
          >
            Remove photo
          </button>
        </div>
      </div>

      {/* Shot-chip strip: tap a chip to arm it, then tap the photo. */}
      <div className="no-print mb-2 flex flex-wrap items-center gap-1">
        {pts.map((p) => {
          const anchored = p.id in anchors;
          const active = pickShotId === p.id;
          return (
            <button
              key={p.id}
              data-testid={`oval-photo-pick-${p.id}`}
              onClick={() => onPickShotId(active ? null : p.id)}
              className={`rounded-sm border px-2 py-1 text-[10px] font-bold uppercase tracking-widest transition-colors ${
                active ? "border-[#F5A623] bg-[#F5A623]/20 text-[#F5A623]"
                : anchored ? "border-[#8DC63F]/60 bg-[#8DC63F]/10 text-[#8DC63F]"
                : "border-[#2A2E39] bg-[#14161A] text-[#8B939A] hover:border-[#009E8E] hover:text-white"
              }`}
              style={{ fontFamily: "JetBrains Mono, monospace" }}
              title={anchored ? "Anchored — tap to re-place" : "Tap then tap the photo to anchor"}
            >
              {anchored ? "✓ " : active ? "◉ " : ""}{p.label}
            </button>
          );
        })}
      </div>

      {/* Photo + overlay SVG */}
      <div className="relative w-full overflow-hidden rounded-sm border border-[#2A2E39] bg-black">
        <img src={photoUrl} alt="Flange" className="block w-full h-auto" data-testid="oval-photo-img"/>
        <svg
          viewBox={`0 0 ${W} ${H}`}
          preserveAspectRatio="none"
          className="absolute inset-0 h-full w-full"
          style={{ cursor: pickShotId ? "crosshair" : "default", pointerEvents: pickShotId ? "auto" : "none" }}
          onClick={onPhotoClick}
          data-testid="oval-photo-overlay"
        >
          {/* Existing anchors (large hollow rings so user sees them) */}
          {Object.entries(anchors).map(([id, [nx, ny]]) => {
            const p = pts.find((q) => q.id === id);
            if (!p) return null;
            const color = colorFor(p.dev_mm);
            return (
              <g key={`anc-${id}`}>
                <circle cx={nx * W} cy={ny * H} r="14" fill="none" stroke={color} strokeWidth="2" strokeDasharray="3 3"/>
                <circle cx={nx * W} cy={ny * H} r="3" fill={color}/>
              </g>
            );
          })}
          {/* Projected deviation dots */}
          {(mode !== "manual" || Object.keys(anchors).length === 0) && pts.map((p) => {
            const [px, py] = project(p.u_mm, p.v_mm);
            const color = colorFor(p.dev_mm);
            const sign = p.dev_mm >= 0 ? "+" : "";
            return (
              <g key={p.id} data-testid={`oval-photo-pt-${p.id}`}>
                <circle cx={px} cy={py} r="9" fill={color} stroke="#0A0B0E" strokeWidth="2" opacity="0.9"/>
                <text x={px} y={py - 13} textAnchor="middle" fill={color} fontSize="12"
                      fontFamily="JetBrains Mono, monospace" fontWeight="700">{p.label}</text>
                <text x={px} y={py + 20} textAnchor="middle" fill={color} fontSize="10"
                      fontFamily="JetBrains Mono, monospace">{sign}{p.dev_mm.toFixed(1)}</text>
              </g>
            );
          })}
        </svg>
      </div>

      {/* Manual sliders — only shown when we haven't got enough anchors. */}
      {mode === "manual" && (
        <div className="mt-3 grid gap-3 no-print sm:grid-cols-4">
          <SliderRow label="X offset" min={-W / 2} max={W / 2} step={1} value={transform.x}
            onChange={(v) => set({ x: v })} suffix="px" testid="oval-photo-x"/>
          <SliderRow label="Y offset" min={-H / 2} max={H / 2} step={1} value={transform.y}
            onChange={(v) => set({ y: v })} suffix="px" testid="oval-photo-y"/>
          <SliderRow label="Scale" min={0.2} max={4} step={0.01} value={transform.scale}
            onChange={(v) => set({ scale: v })} suffix="×" testid="oval-photo-scale"/>
          <SliderRow label="Rotation" min={-180} max={180} step={0.5} value={transform.rot}
            onChange={(v) => set({ rot: v })} suffix="°" testid="oval-photo-rot"/>
        </div>
      )}
    </div>
  );
};

const SliderRow = ({ label, min, max, step, value, onChange, suffix, testid }) => (
  <div>
    <div className="flex items-center justify-between text-[9px] uppercase tracking-widest text-[#8B939A]"
         style={{ fontFamily: "Chakra Petch, sans-serif" }}>
      <span>{label}</span>
      <span className="text-white" style={{ fontFamily: "JetBrains Mono, monospace" }}>
        {typeof value === "number" ? value.toFixed(step < 1 ? 2 : 0) : value}{suffix}
      </span>
    </div>
    <input
      type="range"
      data-testid={testid}
      min={min} max={max} step={step}
      value={value}
      onChange={(e) => onChange(parseFloat(e.target.value))}
      className="w-full accent-[#009E8E]"
    />
  </div>
);
